import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import uuid from 'uuid';
import { compose } from 'recompose';
import { Loader, Card, Divider } from 'semantic-ui-react';
import Pagination from 'dyzio-shared-components/lib/components/pagination';

import createNewCampaign from '../../utils/create-new-campaign';

import CampaignsTable from '../../components/campaigns-table';
import TabActionBar, { ViewModes } from '../../components/tab-action-bar';
import CampaignCard from '../../components/campaign-card';
import DyzioEmptyState from '../../components/dyzio-empty-state';
import AddCampaignModal from '../../components/new-add-campaign-modal';
import { DyzioWarningMessage } from '../../components/dyzio-messages';

import hasRESTClient from '../../hocs/has-rest-client';
import hasUserInfo from '../../hocs/has-user-info';
import hasJSONAPIClient from '../../hocs/has-jsonapi-client';
import hasLocalStorageClient from '../../hocs/has-localstorage-client';
import hasViewModeManagement from '../../hocs/has-viewmode-management';

const CAMPAIGN_VERSION_TWO = 2;
const DEFAULT_PER_PAGE = 8;
const HELP_PAGE_URL = 'https://help.dyzio.co/campaigns/campaign-list';

const BRAND_SEARCH_DEBOUNCE_MS = 250;
const BRAND_SEARCH_LIMIT = 100;
const DEFAULT_BRAND_COUNT = 10;
const DEFAULT_BRAND_FETCH_LIMIT = 50;

const perPageOptions = [
  { key: '10', text: '10', value: 10 },
  { key: '20', text: '20', value: 20 },
  { key: '50', text: '50', value: 50 },
];

const campaignStates = [
  { key: 'ACTIVE', text: 'Active', value: 'ACTIVE' },
  { key: 'ARCHIVED', text: 'Archived', value: 'ARCHIVED' },
];

const DEFAULT_SORT = { field: 'campaignCreatedDate', order: 'DESC' };

const INITIAL_FORM_STATE_FIELDS = {
  name: null,
  budget: null,
  liveDate: null,
  hashtags: null,
  endDate: null,
  version: 2,
  hasChanged: false,
  savedOk: false,
  isValidBudget: true,
};

class CampaignsList extends React.Component {
  static contextTypes = {
    router: PropTypes.object,
  };

  constructor(props) {
    super(props);

    console.log('CampaignsList constructor');
    const perPage = props.dyzioStorage.get(props.dyzioStorage.KEYS.CAMPAIGNS_LIST_PER_PAGE);
    const campaignFilter = props.dyzioStorage.get(props.dyzioStorage.KEYS.CAMPAIGNS_LIST_FILTER);
    this.state = {
      isLoadingCampaigns: true,
      isLoading: true,
      isFailed: false,
      isLoaded: false,
      isTimeoutError: false,
      records: [],
      totalRecords: 0,
      campaignFilter: campaignFilter.value || 'ACTIVE',
      viewMode: ViewModes.table,
      currentPage: 1,
      createdByOptions: [],
      brandOptions: [],
      defaultBrandOptions: [],
      isSearchingBrands: false,
      search: {

      },
      perPage: perPage.value || DEFAULT_PER_PAGE,
      addCampaignModalVisible: false,
      filter: {
        version: CAMPAIGN_VERSION_TWO,
      },
      // new campaign fields
      ...INITIAL_FORM_STATE_FIELDS,
    };
  }

  componentDidMount = () => {
    console.log('CampaignsList componentDidMount');
    this.requestData();
    this.loadOrgUsers();
    this.loadDefaultBrands();
  }

  componentWillUnmount() {
    if (this.searchBrandsDebounced) this.searchBrandsDebounced.cancel();
  }

  applyBrandResults = (requestId, rawBrands) => {
    if (requestId !== this.brandSearchRequestId) return;
    const brands = _.chain(rawBrands)
      .filter(Boolean)
      .uniq()
      .sort()
      .map(b => ({ key: b, value: b, text: b }))
      .value();
    this.setState({ brandOptions: brands, isSearchingBrands: false });
  }

  loadDefaultBrands = () => {
    const { jsonAPIClient, userInfo } = this.props;
    const { userId, organisationId, isUserSuperAdmin } = userInfo;

    const setDefaults = rawBrands => {
      const brands = _.chain(rawBrands)
        .filter(Boolean)
        .uniq()
        .take(DEFAULT_BRAND_COUNT)
        .sort()
        .map(b => ({ key: b, value: b, text: b }))
        .value();
      this.setState(prev => ({
        defaultBrandOptions: brands,
        brandOptions: prev.brandOptions.length ? prev.brandOptions : brands,
      }));
    };

    if (isUserSuperAdmin) {
      jsonAPIClient.find('campaigns', {
        filter: { owner: organisationId },
        fields: { campaigns: 'brand' },
        sort: '-createdDate',
        page: { limit: DEFAULT_BRAND_FETCH_LIMIT },
      }, (err, campaigns) => {
        if (err || !campaigns) return;
        setDefaults(campaigns.map(c => c.toJSONTree().brand));
      });
      return;
    }

    jsonAPIClient.find('campaignPermission', {
      filter: { user: userId, status: 'ACTIVE' },
      include: ['campaign'],
      fields: { campaigns: 'brand' },
      sort: '-createdDate',
      page: { limit: DEFAULT_BRAND_FETCH_LIMIT },
    }, (err, permissions) => {
      if (err || !permissions) return;
      setDefaults(permissions.map(p => {
        const tree = p.toJSONTree();
        return tree.campaign && tree.campaign.brand;
      }));
    });
  }

  searchBrandsDebounced = _.debounce(query => {
    const { jsonAPIClient, userInfo } = this.props;
    const { userId, organisationId, isUserSuperAdmin } = userInfo;
    const trimmed = (query || '').trim();
    if (!trimmed) {
      this.setState({ brandOptions: [], isSearchingBrands: false });
      return;
    }
    this.brandSearchRequestId += 1;
    const requestId = this.brandSearchRequestId;

    if (isUserSuperAdmin) {
      jsonAPIClient.find('campaigns', {
        filter: { owner: organisationId, brand: `:${trimmed}` },
        fields: { campaigns: 'brand' },
        page: { limit: BRAND_SEARCH_LIMIT },
      }, (err, campaigns) => {
        if (err || !campaigns) {
          if (requestId === this.brandSearchRequestId) this.setState({ isSearchingBrands: false });
          return;
        }
        this.applyBrandResults(requestId, campaigns.map(c => c.toJSONTree().brand));
      });
      return;
    }

    jsonAPIClient.find('campaignPermission', {
      filter: {
        user: userId,
        status: 'ACTIVE',
        campaign: { brand: `:${trimmed}` },
      },
      include: ['campaign'],
      fields: { campaigns: 'brand' },
      page: { limit: BRAND_SEARCH_LIMIT },
    }, (err, permissions) => {
      if (err || !permissions) {
        if (requestId === this.brandSearchRequestId) this.setState({ isSearchingBrands: false });
        return;
      }
      this.applyBrandResults(requestId, permissions.map(p => {
        const tree = p.toJSONTree();
        return tree.campaign && tree.campaign.brand;
      }));
    });
  }, BRAND_SEARCH_DEBOUNCE_MS)

  brandSearchRequestId = 0;

  onBrandSearch = query => {
    if (!(query || '').trim()) {
      this.searchBrandsDebounced.cancel();
      this.brandSearchRequestId += 1;
      this.setState(prev => ({ brandOptions: prev.defaultBrandOptions, isSearchingBrands: false }));
      return;
    }
    this.setState({ isSearchingBrands: true });
    this.searchBrandsDebounced(query);
  }

  loadOrgUsers = () => {
    const { jsonAPIClient, userInfo: { organisationId, name: currentUserName } } = this.props;
    if (!organisationId) return;
    jsonAPIClient.find('users', { filter: { organisation: organisationId } }, (err, users) => {
      if (err || !users) return;
      const sortedNames = _.chain(users)
        .map(u => u.toJSONTree().name)
        .filter(Boolean)
        .uniq()
        .sort()
        .value();
      const orderedNames = currentUserName && _.includes(sortedNames, currentUserName)
        ? [currentUserName, ..._.without(sortedNames, currentUserName)]
        : sortedNames;
      const options = _.map(orderedNames, name => ({
        key: name,
        value: name,
        text: name === currentUserName ? `${name} (you)` : name,
      }));
      this.setState({ createdByOptions: options });
    });
  }

  onInputChange = (event, { name, value }) => {
    this.setState({
      [name]: value,
      hasChanged: true,
      savedOk: false,
    });
  }

  onLiveDateChange = date => {
    this.setState({
      liveDate: date,
      hasChanged: true,
      savedOk: false,
    });
  }

  onEndDateChange = date => {
    this.setState({
      endDate: date,
      hasChanged: true,
      savedOk: false,
    });
  }

  onDeleteCampaign = id => {
    if (id) {
      this.updateCampaign(id, {
        attributes: {
          status: 'DELETED',
        },
      });
    }
  }

  onArchiveCampaign = async id => {
    if (id) {
      const { restClient, userInfo: { organisationId } } = this.props;
      await restClient({
        method: 'DELETE',
        url: `/organisations/${organisationId}/cache`,
        baseURL: process.env.REACT_APP_CAMPAIGNS_GET_URL,
      });
      this.updateCampaign(id, {
        attributes: {
          status: 'ARCHIVED',
        },
      });
    }
  }

  onUnarchiveCampaign = id => {
    if (id) {
      this.updateCampaign(id, {
        attributes: {
          status: 'ACTIVE',
        },
      });
    }
  }

  onCurrencyChange = (event, data) => {
    const { value } = data;
    this.setState({
      currency: value,
    });
  }

  onPageIncrement = () => {
    this.setState(prevState => ({
      currentPage: prevState.currentPage + 1,
    }), this.requestData);
  }

  onPageDecrement = () => {
    this.setState(prevState => ({
      currentPage: prevState.currentPage - 1,
    }), this.requestData);
  }

  onPerPageValueChanged = (event, { value }) => {
    const { dyzioStorage, viewModeManager } = this.props;
    this.setState({
      perPage: value,
    }, () => {
      viewModeManager.updateViewMode(dyzioStorage.KEYS.CAMPAIGNS_LIST_PER_PAGE, value);
      this.requestData();
    });
  }

  onSearchInputSubmit = value => {
    this.setState({
      search: {
        searchTerm: value,
      },
    }, this.requestData);
  }

  onSearchCleared = () => {
    this.setState({
      search: {},
    }, this.requestData);
  }

  toggleAddNewCampaignModalState = () => {
    const { addCampaignModalVisible } = this.state;
    const updatedState = {
      addCampaignModalVisible: !addCampaignModalVisible,
    };
    if (addCampaignModalVisible) {
      _.assign(updatedState, INITIAL_FORM_STATE_FIELDS);
    }
    this.setState(updatedState);
  }

  requestData = () => {
    const { restClient, userInfo } = this.props;
    const { userId, organisationId, isUserSuperAdmin } = userInfo;
    const { currentPage, perPage, search, campaignFilter, filter } = this.state;

    let options = {
      perPage,
      currentPage,
      search,
      sort: DEFAULT_SORT,
      filter: {
        campaignPermissionStatus: campaignFilter,
        campaignBrand: filter.campaignBrand || undefined,
        campaignCreatedBy: filter.campaignCreatedBy || undefined,
      },
    };

    // if there is a search then force the results page back to the start
    // should use setState here or disable the pagination controls on a search
    if (search.searchTerm) options.currentPage = 1;

    let campaignQueryUrl;

    if (isUserSuperAdmin) {
      campaignQueryUrl = `/organisations/${organisationId}/campaigns?q=`;
    } else {
      options = _.assignIn(options, { userId });
      campaignQueryUrl = '/campaigns/query?q=';
    }

    const queryParams = encodeURIComponent(JSON.stringify(options));
    this.setState({
      isLoaded: false,
      isFailed: false,
      isLoading: true,
    }, () => {
      const restRequest = restClient({
        method: 'get',
        url: `${campaignQueryUrl}${queryParams}`,
        baseURL: process.env.REACT_APP_CAMPAIGNS_GET_URL,
      }).then(response => {
        const { data: { data: { data, results } } } = response;
        return this.setState({
          records: data,
          isLoadingCampaigns: false,
          totalRecords: results,
        });
      });

      const orgPromise = restClient.get(`organisations/${organisationId}`)
        .then(organisationResource => {
          const {
            data: {
              data: { attributes },
            },
          } = organisationResource;
          const { defaultCurrency } = attributes;
          return this.setState({ organisation: attributes, defaultCurrency, currency: defaultCurrency });
        });

      Promise.all([restRequest, orgPromise])
        .then(() => this.setState({
          isLoaded: true,
          isFailed: false,
          isLoading: false,
        }))
        .catch(error => {
          this.setState({
            isLoadingCampaigns: false,
            isLoaded: true,
            isTimeoutError: error && _.includes(error.toString(), 'timeout'),
            isFailed: true,
            isLoading: false,
          });
        });
    });
  }

  onCampaignFilterChanged = (event, { value }) => {
    const { dyzioStorage, viewModeManager } = this.props;
    this.setState({
      campaignFilter: value,
    }, () => {
      viewModeManager.updateViewMode(dyzioStorage.KEYS.CAMPAIGNS_LIST_FILTER, value);
      this.requestData();
    });
  }

  updateCampaign = (id, data) => {
    const { restClient } = this.props;
    restClient.patch(`/campaignPermission/${id}`, {
      data,
    }).then(() => {
      this.requestData();
    });
  }

  onAddCampaignSubmit = values => {
    const {
      organisation,
    } = this.state;

    const {
      jsonAPIClient,
      userInfo,
    } = this.props;

    return createNewCampaign({
      ...values,
      jsonAPIClient,
      userInfo,
      organisation,
    });
  };

  onAddFilter = (field, value) => {
    this.setState(prev => ({
      filter: { ...prev.filter, [field]: value },
      currentPage: 1,
    }), this.requestData);
  }

  onRemoveFilter = field => {
    this.setState(prev => ({
      filter: { ...prev.filter, [field]: undefined },
      currentPage: 1,
    }), this.requestData);
  }

  onInlineFilterChange = (field, value) => {
    if (value) {
      this.onAddFilter(field, value);
    } else {
      this.onRemoveFilter(field);
    }
  }

  render() {
    const actionButtonProps = {
      onClick: this.toggleAddNewCampaignModalState,
      content: 'Add a campaign',
    };

    const { userInfo: { email, name: userName, userId, isUserSuperAdmin } } = this.props;

    const messageProps = {
      email, userName, userId,
    };

    const {
      isLoading,
      isLoaded,
      isFailed,
      records,
      addCampaignModalVisible,
      currentPage,
      perPage,
      totalRecords,
      campaignFilter,
      isLoadingCampaigns,
      search: { searchTerm },
      isTimeoutError,
      filter,
      createdByOptions,
      brandOptions,
      isSearchingBrands,
    } = this.state;

    const inlineSelectFilters = [
      {
        field: 'campaignBrand',
        label: 'Brand',
        emptyLabel: 'Filter by brand',
        searchPlaceholder: 'Search brand',
        options: brandOptions,
        value: filter.campaignBrand,
        allowAdditions: true,
        suggestionsLabel: 'Suggestions',
        onSearch: this.onBrandSearch,
        isSearching: isSearchingBrands,
        noResultsMessage: 'No matching brands',
      },
      {
        field: 'campaignCreatedBy',
        label: 'Owner',
        emptyLabel: 'Filter by owner',
        searchPlaceholder: 'Search owner',
        options: createdByOptions,
        value: filter.campaignCreatedBy,
        suggestionsLabel: 'Suggestions',
      },
    ];

    const totalPages = _.ceil(totalRecords / perPage);
    const isSearchContext = searchTerm && searchTerm.length > 0;

    const noCampaignPermissions = _.isEmpty(records);
    const isLoadSuccess = isLoaded && !isFailed && !noCampaignPermissions;
    const isLoadEmptyData = isLoaded && !isFailed && noCampaignPermissions;

    return (
      <div>
        <TabActionBar
          hasSmallCardsView={false}
          hasLargeCardsView={false}
          hasTableView={false}
          hasSearchBox
          hasCampaignFilter
          hasActionButton
          hasPerPageSelector
          actionButtonProps={actionButtonProps}
          shouldDisableButtons={isLoading || noCampaignPermissions}
          onPerPageValueChanged={this.onPerPageValueChanged}
          onSearchInputSubmit={this.onSearchInputSubmit}
          onSearchCleared={this.onSearchCleared}
          onCampaignFilterChanged={this.onCampaignFilterChanged}
          perPage={perPage}
          helpPageUrl={HELP_PAGE_URL}
          perPageOptions={perPageOptions}
          campaignFilterOptions={campaignStates}
          campaignFilter={campaignFilter}
          inlineSelectFilters={inlineSelectFilters}
          onInlineFilterChange={this.onInlineFilterChange}
        />
        {
          isLoadSuccess &&
          <div>
            <Pagination
              onPageIncrement={this.onPageIncrement}
              onPageDecrement={this.onPageDecrement}
              currentPage={currentPage}
              totalPages={totalPages}
            />
            <div style={{ clear: 'both', height: '5px' }} />
          </div>
        }
        { isLoading && <Loader active content="Loading campaigns..." /> }
        {
          isTimeoutError && <DyzioWarningMessage
            header="Sorry... Your request timed out!"
            content="Our tech team has been informed and will look into the issue for you."
            reportMessage="Loading campaigns timeout"
            reportMessageArgs={messageProps}
          />
        }
        {
          isLoadEmptyData &&
          <DyzioEmptyState
            isSearchContext={isSearchContext}
            buttonLabel="Add a campaign"
            helpUrl="https://help.dyzio.co/en/articles/5089448-how-to-create-a-campaign"
            context="CAMPAIGNS"
            onClick={this.toggleAddNewCampaignModalState}
            content={
              <div>
                <p>{ !isSearchContext ? 'You have no campaigns.' : 'No search results!' }</p>
                {
                  !isSearchContext &&
                  <p>Use the button below to start your first campaign.</p>
                }
              </div>
            }
          />
        }
        {
          isLoadSuccess &&
            <CampaignsTable
              records={records}
              isLoadingCampaigns={isLoadingCampaigns}
              onDelete={this.onDeleteCampaign}
              onArchive={this.onArchiveCampaign}
              onUnarchive={this.onUnarchiveCampaign}
              listType={campaignFilter}
              isUserSuperAdmin={isUserSuperAdmin}
            />
        }
        {
          isLoadSuccess &&
          <div>
            <Pagination
              onPageIncrement={this.onPageIncrement}
              onPageDecrement={this.onPageDecrement}
              currentPage={currentPage}
              totalPages={totalPages}
            />
            <div style={{ clear: 'both', height: '5px' }} />
          </div>
        }
        {
          addCampaignModalVisible &&
          <AddCampaignModal
            onClose={this.toggleAddNewCampaignModalState}
            onAddCampaignSubmit={this.onAddCampaignSubmit}
            allowCampaignTypeChange
          />
        }
      </div>
    );
  }
}

CampaignsList.propTypes = {
  dyzioStorage: PropTypes.object.isRequired,
  viewModeManager: PropTypes.object.isRequired,
  userInfo: PropTypes.object.isRequired,
  jsonAPIClient: PropTypes.object.isRequired,
  restClient: PropTypes.func.isRequired,
};


export default compose(
  hasRESTClient,
  hasJSONAPIClient,
  hasUserInfo,
  hasLocalStorageClient,
  hasViewModeManagement,
)(CampaignsList);



/** WEBPACK FOOTER **
 ** ./src/containers/campaign-list/index.js
 **/